/**
 * Add-to-calendar helpers for the event. Both build from the public settings
 * so the dates and venue stay in sync with what the admin entered.
 */
export interface CalendarEvent {
  title: string;
  /** ISO dates (YYYY-MM-DD); all-day span, end is inclusive. */
  start: string;
  end: string;
  location?: string | null;
  description?: string;
  url?: string;
}

const compact = (iso: string): string => iso.replace(/-/g, '');

/** Google Calendar's all-day range is end-exclusive, so bump the end by a day. */
function nextDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function googleCalendarUrl(e: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: e.title,
    dates: `${compact(e.start)}/${compact(nextDay(e.end))}`,
  });
  if (e.location) params.set('location', e.location);
  const details = [e.description, e.url].filter(Boolean).join('\n');
  if (details) params.set('details', details);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

const escapeIcs = (s: string): string =>
  s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

/** RFC 5545 all-day event; works with Apple Calendar, Outlook, and Google via import. */
export function icsFile(e: CalendarEvent): string {
  const stamp = new Date()
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BrandonStrong.com//Event//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${compact(e.start)}-${escapeIcs(e.title).replace(/\s+/g, '-').toLowerCase()}@brandonstrong.com`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${compact(e.start)}`,
    `DTEND;VALUE=DATE:${compact(nextDay(e.end))}`,
    `SUMMARY:${escapeIcs(e.title)}`,
  ];
  if (e.location) lines.push(`LOCATION:${escapeIcs(e.location)}`);
  const description = [e.description, e.url].filter(Boolean).join('\n');
  if (description) lines.push(`DESCRIPTION:${escapeIcs(description)}`);
  if (e.url) lines.push(`URL:${e.url}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n') + '\r\n';
}

export function icsDataUrl(e: CalendarEvent): string {
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(icsFile(e))}`;
}
